import React, { useEffect, useState } from "react";
import {
  Card,
  Grid,
  Button,
  Box,
  Typography,
  Snackbar,
  Slide,
  Alert,
  FormControl,
  CircularProgress,
} from "@mui/material";
import SoftBox from "components/SoftBox";
import logo from "assets/images/Anser.png";
import cartoonImage from "assets/images/Chat bot-bro.svg";
import successCartoon from "assets/images/Mailbox-bro.svg";
import doneCartoon from "assets/images/Completed steps-rafiki.svg";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import axios from "axios";
import { FAST_API_BASE_URL } from "constants";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "context/AuthContext";
import encryptedStorage from "utils/encryptedStorage";

const Onboarding = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [isLogin, setIsLogin] = useState(true);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [forgotMessage, setForgotMessage] = useState("");

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [redirecting, setRedirecting] = useState(true);
  const [hasCode, setHasCode] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [googleSignInLoading, setGoogleSignInLoading] = useState(false);

  const generateOAuthUrl = () => {
    const clientId = process.env.REACT_APP_WEBFLOW_CLIENT_ID; 
    const scopes = "assets:read cms:read custom_code:read custom_code:write pages:read sites:read site_config:read components:read";
    
    const params = new URLSearchParams({
      client_id: clientId,
      response_type: "code",
      scope: scopes,
      prompt: "consent",
    });
    
    return `https://webflow.com/oauth/authorize?${params.toString()}`;
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const error = params.get("error");
    
    if (error) {
      console.error("OAuth error:", error);
      setSnackbarMessage(`Authorization failed: ${error}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setRedirecting(false);
      return;
    }
    
    if (code) {
      try {
        encryptedStorage.setItem('webflow_auth_code', code);
        
        window.history.replaceState({}, document.title, window.location.pathname);
        
        setHasCode(true);
        setRedirecting(false);
        setIsLogin(false); 
      } catch (error) {
        console.error('Failed to save auth code:', error);
        setRedirecting(false);
      }
    } else {
      const hasStoredCode = encryptedStorage.getItem('webflow_auth_code');
      
      if (!hasStoredCode) {
        console.log('No auth code found, redirecting to Webflow...');
        const authUrl = generateOAuthUrl();
        window.location.href = authUrl;
        return;
      } else {
        setHasCode(true);
        setRedirecting(false);
      }
    }
  }, []);

  if (redirecting) {
    return (
      <SoftBox
        height="100vh"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        bgColor="#f0f4f7"
      >
        <img src={logo} alt="Ansera Logo" style={{ height: "4rem", marginBottom: "2rem" }} />
        <Card sx={{ padding: 4, textAlign: "center", maxWidth: 400, width: "100%" }}>
          <CircularProgress size={60} sx={{ color: "#219297", mb: 3 }} />
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Connecting to Webflow...
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Please wait while we connect to Webflow for authorization.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontSize: "0.9rem" }}>
            This may take a few moments
          </Typography>
        </Card>
      </SoftBox>
    );
  }

const handleGoogleSignIn = async (credentialResponse) => {
  setGoogleSignInLoading(true);
  try {
    const decoded = jwtDecode(credentialResponse.credential);
    
    const code = encryptedStorage.getItem('webflow_auth_code');
    
    if (code) {
      const payload = {
        code: code,
        email: decoded.email,
        name: decoded.name,
        profile_pic: decoded.picture,
        type: "google"
      };

      const response = await axios.post(
        `${FAST_API_BASE_URL}/api/webflow/callback`,
        payload,
        {
          headers: {
            "X-API-KEY": process.env.REACT_APP_API_KEY,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        const { result } = response.data;
        const isExistingUser = response.data.messages && 
                              (response.data.messages.includes("existing") || 
                               response.data.messages.includes("login") ||
                               response.data.messages.includes("Welcome"));
        
        if (login && result) {
          await login(
            {
              id: result.user_id || result.id,
              email: decoded.email,
              name: result.name || decoded.name,
              is_super_admin: result.is_super_admin === 1,
              is_whitelabel_user: result.is_whitelabel_user
            },
            result.domains || []
          );
        }
        
        if (result && result.domain_id) {
          try {
            encryptedStorage.setItem('domain_token', result.domain_id);
          } catch (storageError) {
            console.error('Failed to save domain token:', storageError);
          }
        }
        
        localStorage.setItem('role', result?.role || (result?.domains && result.domains[0]?.role) || "admin");
        localStorage.setItem('source', 'webflow');
        
        if (isExistingUser) {
          setSnackbarMessage("Google login successful! Welcome back.");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
        } else {
          setSuccess(true);
          setSnackbarMessage("Registration successful with Google!");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
        }
        
        setTimeout(() => {
          navigate("/settings");
        }, 1000);
      }
    } else {
      const payload = {
        mail: decoded.email,
        name: decoded.name,
        profile_pic: decoded.picture,
        type: "google",
        role: ""
      };

      const response = await axios.post(
        `${FAST_API_BASE_URL}/api/user/re-login`, 
        payload
      );

      if (response.status === 200 && response.data) {
        const { result } = response.data;
        
        if (login) {
          await login(
            {
              id: result.user_id,
              email: decoded.email,
              name: result.name,
              is_super_admin: result.is_super_admin === 1,
              is_whitelabel_user: result.is_whitelabel_user
            },
            result.domains || []
          );
        }
        
        setSnackbarMessage("Google login successful! Please wait...");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        
        setTimeout(() => {
          if (result.is_super_admin === 1) {
            navigate("/customers");
          } else if (result.domains && result.domains.length > 0) {
            const role = result.domains[0]?.role;
            if (role === "read only") {
              navigate("/dashboard");
            } else {
              navigate("/settings");
            }
          } else {
            navigate("/dashboard");
          }
        }, 1000);
      }
    }
  } catch (error) {
    console.error("Google sign-in failed:", error);
    
    const code = encryptedStorage.getItem('webflow_auth_code');
    if (code) {
      if (error.response && error.response.status === 401) {
        setSnackbarMessage("Invalid credentials. Please try again.", "error");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } else if (error.response && error.response.status === 409) {
        setSnackbarMessage("An account with this email already exists. Please try logging in.", "error");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage("Google sign-in failed. Please try again.", "error");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } else {
      if (error.response && error.response.status === 401) {
        const decoded = jwtDecode(credentialResponse.credential);
        setFormData(prev => ({
          ...prev,
          email: decoded.email
        }));
        setIsLogin(false);
        setSnackbarMessage("Please complete signup with your Google account");
        setSnackbarSeverity("info");
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage("Google sign-in failed. Please try again.", "error");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    }
  } finally {
    setGoogleSignInLoading(false);
  }
};

  const enterForgotMode = () => {
    setForgotMode(true);
    setForgotEmail("");
    setForgotMessage("");
    setForgotSuccess(false);
    setFormData({ email: "", password: "", confirmPassword: "" });
  };

  const exitForgotMode = () => {
    setForgotMode(false);
    setForgotEmail("");
    setForgotMessage("");
    setForgotSuccess(false);
  };

  const handleForgotEmailChange = (e) => {
    setForgotEmail(e.target.value);
    setForgotMessage("");
  };

  const handleSendReset = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      setForgotMessage("Please enter your email.");
      return;
    }

    setForgotLoading(true);
    try {
      const payload = { email: forgotEmail };
      const response = await axios.post(
        `${FAST_API_BASE_URL}/api/reset-password`,
        payload
      );

      if (response && response.status === 200) {
        setForgotSuccess(true);
        setForgotMessage("We have sent you an email to reset your password..");
      } else {
        setForgotSuccess(false);
        setForgotMessage("Failed to send reset link/ Email doesn't exist. Please try again.");
      }
    } catch (err) {
      console.error("Send reset failed:", err);
      setForgotSuccess(false);
      setForgotMessage("Failed to send reset link/ Email doesn't exist. Please try again.");
    } finally {
      setForgotLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const passwordRules = {
    minLength: formData.password.length >= 8,
    number: /[0-9]/.test(formData.password),
    specialChar: /[^A-Za-z0-9]/.test(formData.password),
    uppercase: /[A-Z]/.test(formData.password),
    lowercase: /[a-z]/.test(formData.password),
  };
  const allRulesPassed = Object.values(passwordRules).every(Boolean);

const handleSubmit = async (e) => {
  e.preventDefault();
  
  const code = encryptedStorage.getItem('webflow_auth_code');
  
  if (!formData.email || !formData.password) {
    alert("Please fill in all fields");
    return;
  }

  if (!isLogin && formData.password !== formData.confirmPassword) {
    alert("Passwords do not match!");
    return;
  }

  if (!isLogin && !allRulesPassed) {
    alert("Password does not meet requirements");
    return;
  }

  setLoading(true);
  try {
    if (code) {
      const payload = {
        code,
        email: formData.email.trim(),
        password: formData.password.trim(),
        type: "custom"
      };

      const response = await axios.post(
        `${FAST_API_BASE_URL}/api/webflow/callback`,
        payload,
        {
          headers: {
            "X-API-KEY": process.env.REACT_APP_API_KEY,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        const { result } = response.data;
        const isExistingUser = response.data.messages && 
                              (response.data.messages.includes("existing") || 
                               response.data.messages.includes("login") ||
                               response.data.messages.includes("Welcome"));
        
        if (login && result) {
          await login(
            {
              id: result.user_id || result.id,
              email: formData.email,
              name: result.name || formData.email.split('@')[0],
              is_super_admin: result.is_super_admin === 1,
              is_whitelabel_user: result.is_whitelabel_user
            },
            result.domains || []
          );
        }
        
        if (result && result.domain_id) {
          try {
            encryptedStorage.setItem('domain_token', result.domain_id);
            // console.log('Saved domain token:', result.domain_id);
          } catch (storageError) {
            console.error('Failed to save domain token:', storageError);
          }
        }
        
        localStorage.setItem('role', result?.role || (result?.domains && result.domains[0]?.role) || "admin");
        localStorage.setItem('source', 'webflow');
        
        if (isExistingUser) {
          setSnackbarMessage("Login successful! Welcome back.");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
        } else {
          setSuccess(true);
          setSnackbarMessage("Registration successful!");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
        }
        
        setTimeout(() => {
          navigate("/settings");
        }, 1000);
      }
    } else {
      const payload = {
        mail: formData.email,
        password: formData.password,
        type: "custom",
        role: isLogin ? "" : "admin"
      };

      let response;
      if (isLogin) {
        response = await axios.post(`${FAST_API_BASE_URL}/api/user/re-login`, payload);
      } else {
        response = await axios.post(`${FAST_API_BASE_URL}/api/user/login`, payload);
      }

      if (response.status === 200 && response.data) {
        const { result } = response.data;
        
        if (login) {
          await login(
            {
              id: result.user_id,
              email: formData.email,
              name: result.name,
              is_super_admin: result.is_super_admin === 1,
              is_whitelabel_user: result.is_whitelabel_user
            },
            result.domains || []
          );
        }
        
        setSnackbarMessage(isLogin ? "Login Successful! Please wait..." : "Registration successful!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setTimeout(() => {
          if (result.is_super_admin === 1) {
            navigate("/customers");
          } else if (result.domains && result.domains.length > 0) {
            const role = result.domains[0]?.role;
            if (role === "read only") {
              navigate("/dashboard");
            } else {
              navigate("/settings");
            }
          } else {
            navigate("/dashboard");
          }
        }, 1000);
      }
    }
  } catch (err) {
    console.error("Authentication failed:", err);
    
    if (code) {
      if (err.response && err.response.status === 401) {
        setSnackbarMessage("Invalid email or password. Please try again.", "error");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } else if (err.response && err.response.status === 409) {
        setSnackbarMessage("An account with this email already exists. Please try logging in.", "error");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        setIsLogin(true); 
      } else {
        const errorMessage = err.response?.data?.messages?.[0] || 
                            err.response?.data?.detail || 
                            "Something went wrong. Please try again.";
        setSnackbarMessage(errorMessage);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } else {
      const errorMessage = err.response?.data?.messages?.[0] || 
                          err.response?.data?.detail || 
                          "Something went wrong. Please try again.";
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  } finally {
    setLoading(false);
  }
};
  const renderRule = (label, passed) => (
    <Box display="flex" alignItems="center" mb={0.5}>
      {passed ? (
        <CheckCircleIcon sx={{ mr: 1, color: "#219297", fontSize: "14px" }} />
      ) : (
        <CancelIcon color="error" fontSize="small" sx={{ mr: 1 }} />
      )}
      <Typography
        variant="body2"
        color={passed ? "#219297" : "text.secondary"}
        sx={{ fontSize: "12px" }}
      >
        {label}
      </Typography>
    </Box>
  );

  const webflowAuthCode = encryptedStorage.getItem('webflow_auth_code');

  return (
    <>
      <SoftBox px={2} py={1} width="100%" bgColor="white" shadow="md">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <img src={logo} alt="Ansera Logo" style={{ height: "3rem" }} />
        </Box>
      </SoftBox>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        TransitionComponent={(props) => <Slide {...props} direction="top" />}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{
            width: "100%",
            color: "#fff",
            backgroundColor:
              snackbarSeverity === "success"
                ? "#008080"
                : snackbarSeverity === "error"
                ? "#d32f2f"
                : undefined,
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <SoftBox
        height="calc(100vh - 5rem)"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bgColor="#f0f4f7"
        px={2}
      >
        <Grid container spacing={4} alignItems="center" justifyContent="center" maxWidth="lg">
          {!isMobile && (
            <Grid item md={6}>
              <Box
                component="img"
                src={success ? doneCartoon : cartoonImage}
                alt="Illustration"
                sx={{ width: "100%", maxWidth: "500px", mx: "auto" }}
              />
            </Grid>
          )}

          <Grid item xs={12} sm={10} md={6}>
            <Card
              sx={{
                padding: 4,
                maxWidth: 480,
                margin: "0 auto",
                boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                borderRadius: 4,
                minHeight: "320px",
              }}
            >
              {forgotMode ? (
                <form onSubmit={handleSendReset}>
                  <Typography
                      variant="body2"
                      color="primary"
                      align="center"
                      sx={{ mt: 1,color:"#219297",fontWeight:"bold" }}
                    >
                      Please Re-install Ansera on your site after resetting the password.
                    </Typography>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <label
                      style={{
                        fontWeight: 500,
                        fontSize: "14px",
                        color: "#555",
                        marginBottom: "6px",
                      }}
                    >
                      Enter your email
                    </label>
                    <input
                      id="forgotEmail"
                      name="forgotEmail"
                      type="email"
                      placeholder="you@company.com"
                      value={forgotEmail}
                      onChange={handleForgotEmailChange}
                      required
                      style={{
                        width: "100%",
                        padding: "12px 14px",
                        borderRadius: "8px",
                        border: "1px solid #ccc",
                        fontSize: "15px",
                      }}
                    />
                  </FormControl>

                  {forgotMessage && (
                    <Typography
                      variant="body2"
                      align="center"
                      sx={{
                        mb: 2,
                        color: forgotSuccess ? "success.main" : "error.main",
                        fontSize: "0.95rem",
                      }}
                    >
                      {forgotMessage}
                    </Typography>
                  )}

                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={forgotLoading || forgotSuccess}
                    sx={{
                      py: { xs: 1, sm: 1.2 },
                      borderRadius: "8px",
                      backgroundColor: forgotSuccess ? "#ccc" : "#219297",
                      color: "#fff",
                      fontWeight: "bold",
                      fontSize: "clamp(0.85rem, 1vw, 1rem)",
                    }}
                  >
                    {forgotLoading ? "Sending..." : forgotSuccess ? "Sent" : "Send reset link"}
                  </Button>
                  
                  <Button
                    variant="text"
                    fullWidth
                    onClick={exitForgotMode}
                    sx={{
                      mt: 2,
                      color: "#219297",
                      textTransform: "none",
                    }}
                  >
                    ← Back to {webflowAuthCode ? "Signup" : "Login"}
                  </Button>
                </form>
              ) : loading ? (
                <Box textAlign="center">
                  <Typography variant="h5" fontWeight="bold" gutterBottom>
                    {webflowAuthCode ? "Registering your site..." : "Authenticating..."}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Please wait
                  </Typography>
                  <Box mt={3} display="flex" justifyContent="center" gap={1}>
                    <span className="dot" />
                    <span className="dot" />
                    <span className="dot" />
                  </Box>
                </Box>
              ) : success ? (
                <Box textAlign="center">
                  <Box
                    component="img"
                    src={successCartoon}
                    alt="Success"
                    sx={{ width: "100%", maxWidth: "300px", mx: "auto", mt: 2 }}
                  />
                  <Typography variant="h6" sx={{ mt: 2 }}>
                    Registration Successful!
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Redirecting to dashboard...
                  </Typography>
                </Box>
              ) : (
                <Grid container direction="column" spacing={3}>
                  <Grid item>
                    <Typography variant="h3" fontWeight="bold" align="center">
                      {webflowAuthCode 
                        ? "Complete Webflow Integration" 
                        : isLogin 
                        ? "Login to Ansera AI" 
                        : "Register for Ansera AI"}
                    </Typography>
                  </Grid>

                  <Grid item>
                    <form onSubmit={handleSubmit} style={{ width: "100%" }}>
                      <div style={{ marginBottom: "20px" }}>
                        <label htmlFor="email" style={{ display: "block", marginBottom: "6px" }}>
                          Email
                        </label>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="you@example.com"
                          style={{
                            width: "100%",
                            padding: "12px 14px",
                            borderRadius: "8px",
                            border: "1px solid #ccc",
                          }}
                        />
                      </div>

                      <div style={{ marginBottom: "20px" }}>
                        <label htmlFor="password" style={{ display: "block", marginBottom: "6px" }}>
                          Password
                        </label>
                        <input
                          id="password"
                          name="password"
                          type="password"
                          value={formData.password}
                          onChange={handleChange}
                          required
                          placeholder="Enter your password"
                          style={{
                            width: "100%",
                            padding: "12px 14px",
                            borderRadius: "8px",
                            border: "1px solid #ccc",
                          }}
                        />
                        {isLogin && !webflowAuthCode && (
                          <Typography
                            variant="body2"
                            color="primary"
                            align="right"
                            sx={{ mt: 1, cursor: "pointer",color:"#219197" }}
                            onClick={enterForgotMode}
                          >
                            Forgot Password?
                          </Typography>
                        )}
                        {!isLogin && formData.password && (
                          <Box mt={1.5}>
                            {renderRule("At least 8 characters", passwordRules.minLength)}
                            {renderRule("Includes a number", passwordRules.number)}
                            {renderRule("Includes a special character", passwordRules.specialChar)}
                            {renderRule("Includes Uppercase [A-Z]", passwordRules.uppercase)}
                            {renderRule("Includes Lowercase [a-z]", passwordRules.lowercase)}
                          </Box>
                        )}
                      </div>

                      {!isLogin && (
                        <div style={{ marginBottom: "20px" }}>
                          <label
                            htmlFor="confirmPassword"
                            style={{ display: "block", marginBottom: "6px" }}
                          >
                            Confirm Password
                          </label>
                          <input
                            id="confirmPassword"
                            name="confirmPassword"
                            type="password"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            required
                            placeholder="Confirm your password"
                            style={{
                              width: "100%",
                              padding: "12px 14px",
                              borderRadius: "8px",
                              border: "1px solid #ccc",
                            }}
                          />
                        </div>
                      )}

                      <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        sx={{
                          padding: "10px 0",
                          borderRadius: "8px",
                          backgroundColor: (!isLogin && !allRulesPassed) ? "#ccc" : "#219297",
                          color: "#ffffff",
                          mb: 2,
                          "&:hover": {
                            backgroundColor: (!isLogin && !allRulesPassed) ? "#ccc" : "#1a7a7d",
                          }
                        }}
                        disabled={!isLogin && !allRulesPassed}
                      >
                        {webflowAuthCode ? "Complete Registration" : isLogin ? "Login" : "Register"}
                      </Button>
                    </form>

                    <Box sx={{ textAlign: "center", mt: 2 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        OR
                      </Typography>
                      <Box sx={{ display: "flex", justifyContent: "center" }}>
                        <GoogleLogin
                          theme="filled_blue"
                          text="continue_with"
                          onSuccess={handleGoogleSignIn}
                          onError={() => {
                            setSnackbarMessage("Google login failed");
                            setSnackbarSeverity("error");
                            setSnackbarOpen(true);
                          }}
                        />
                      </Box>
                      {googleSignInLoading && (
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                          <CircularProgress size={24} color="teal"/>
                          <Typography variant="body2">
                            Signing in with Google...
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    {webflowAuthCode && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        align="center"
                        sx={{ mt: 2, cursor: "pointer" }}
                        onClick={() => {
                          setIsLogin(prev => !prev);
    
      
                        }}
                      >
                        {isLogin ? (
                          <>
                            Don't have an account?{" "}
                            <span style={{ fontWeight: "bold", color: "#219197" }}>Signup</span>
                          </>
                        ) : (
                          <>
                            Already a user?{" "}
                            <span style={{ fontWeight: "bold", color: "#219197" }}>
                              Login with your credentials
                            </span>
                          </>
                        )}
                      </Typography>
                    )}

                    {/* ADDED: For non-webflow users, show the toggle as before */}
                    {!webflowAuthCode && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        align="center"
                        sx={{ mt: 2, cursor: "pointer" }}
                        onClick={() => setIsLogin((prev) => !prev)}
                      >
                        {isLogin ? (
                          <>
                            Don't have an account?{" "}
                            <span style={{ fontWeight: "bold", color: "#219197" }}>Signup</span>
                          </>
                        ) : (
                          <>
                            Already a user?{" "}
                            <span style={{ fontWeight: "bold", color: "#219197" }}>
                              Login with your credentials
                            </span>
                          </>
                        )}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              )}
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </>
  );
};

export default Onboarding;